import React, { useState, useMemo, useEffect, useLayoutEffect, useRef } from "react";
import { toast } from "sonner";
import { ZoomIn, ZoomOut, Maximize2, Pencil, X, Check, LayoutGrid, GitBranch } from "lucide-react";
import api, { formatDetail } from "@/lib/api";
import { DIVISION_COLOR, DIVISION_ORDER } from "@/lib/constants";
import OrgChartNode from "./OrgChartNode";

// Matrix layout: divisions = columns, levels (Lv1..Lv3) = rows, tier bands = leftmost column
// spanning grid-rows. Rows auto-size so tier bands always line up with staff boxes.
export default function OrgChartVisual({ roots, query, canManage, canEditTiers, onSelect, viewerId, canSeeAllLevels = true }) {
  const [zoom, setZoom] = useState(1);
  const [layout, setLayout] = useState("matrix"); // "matrix" | "tree"
  const q = (query || "").toLowerCase();

  const [tiers, setTiers] = useState([]);
  const [editing, setEditing] = useState(null);

  const loadTiers = async () => {
    try {
      const { data } = await api.get("/org-tiers");
      setTiers(data);
    } catch (e) {
      toast.error(formatDetail(e.response?.data?.detail));
    }
  };
  useEffect(() => { loadTiers(); }, []);

  const allUsers = useMemo(() => {
    const out = [];
    const walk = (arr) => arr.forEach((n) => { out.push(n); if (n.children && n.children.length) walk(n.children); });
    walk(roots);
    return out;
  }, [roots]);

  const divisionColumns = useMemo(() => {
    const divs = Array.from(new Set(allUsers.map((u) => u.division).filter(Boolean)));
    divs.sort((a, b) => (DIVISION_ORDER[a] ?? 99) - (DIVISION_ORDER[b] ?? 99) || a.localeCompare(b));
    return divs.map((d) => ({
      name: d,
      users: allUsers
        .filter((u) => u.division === d)
        .sort((a, b) => a.level - b.level || (a.name || "").localeCompare(b.name || "")),
    }));
  }, [allUsers]);

  const treeMaxDepth = useMemo(() => {
    const walk = (arr, d) => arr.reduce((mx, n) => Math.max(mx, n.children && n.children.length ? walk(n.children, d + 1) : d), d);
    return walk(roots, 0);
  }, [roots]);

  // Matrix always has exactly 3 level rows: Lv1, Lv2, Lv3 → max depth = 2
  const maxDepth = layout === "matrix" ? 2 : treeMaxDepth;

  const sortedTiers = useMemo(() => {
    return [...tiers].sort((a, b) => (a.order ?? 0) - (b.order ?? 0) || a.depth_start - b.depth_start);
  }, [tiers]);

  // Bands, with depth clamped to matrix rows (max 3 rows in matrix mode)
  const bands = sortedTiers
    .map((t) => {
      const startD = Math.min(t.depth_start, maxDepth);
      const endD = Math.max(startD, Math.min(t.depth_end, maxDepth));
      return { ...t, startD, endD };
    })
    .filter((b) => b.depth_start <= maxDepth);

  const openAdd = () => setEditing({ mode: "create", label: "", depth_start: 0, depth_end: 0, bg_color: "#DBEAFE" });
  const openEdit = (t) => setEditing({ mode: "edit", id: t.id, label: t.label, depth_start: t.depth_start, depth_end: t.depth_end, bg_color: t.bg_color });
  const close = () => setEditing(null);

  const saveTier = async () => {
    if (!editing) return;
    if (!editing.label.trim()) { toast.error("Label wajib diisi"); return; }
    if (editing.depth_end < editing.depth_start) { toast.error("Depth end harus ≥ depth start"); return; }
    try {
      if (editing.mode === "create") {
        await api.post("/org-tiers", { label: editing.label.trim(), depth_start: Number(editing.depth_start), depth_end: Number(editing.depth_end), bg_color: editing.bg_color });
        toast.success("Tier ditambahkan");
      } else {
        await api.patch(`/org-tiers/${editing.id}`, { label: editing.label.trim(), depth_start: Number(editing.depth_start), depth_end: Number(editing.depth_end), bg_color: editing.bg_color });
        toast.success("Tier diperbarui");
      }
      close();
      loadTiers();
    } catch (e) {
      toast.error(formatDetail(e.response?.data?.detail));
    }
  };

  return (
    <div className="border border-slate-200 bg-white relative" data-testid="org-chart-visual">
      {/* Toolbar */}
      <div className="flex items-center justify-between gap-2 px-3 py-2 bg-white border-b border-slate-200 sticky top-0 z-20">
        <div className="text-[11px] text-slate-500">
          <span className="font-semibold text-[#0B1D3A]">Bagan Struktur</span>
          <span className="ml-2">
            — {countNodes(roots)} personil
            {layout === "matrix" ? `, ${divisionColumns.length} divisi` : `, ${roots.length} root, kedalaman ${treeMaxDepth + 1}`}
          </span>
        </div>
        <div className="flex items-center gap-1">
          <div className="inline-flex border border-slate-300 mr-2">
            <button
              type="button"
              data-testid="org-layout-matrix"
              onClick={() => setLayout("matrix")}
              className={`h-7 px-2 text-[10px] uppercase tracking-wider font-semibold flex items-center gap-1 ${layout === "matrix" ? "bg-[#0B1D3A] text-white" : "bg-white text-slate-600 hover:bg-slate-50"}`}
              title="Kelompokkan per divisi"
            ><LayoutGrid size={11} /> Divisi</button>
            <button
              type="button"
              data-testid="org-layout-tree"
              onClick={() => setLayout("tree")}
              className={`h-7 px-2 text-[10px] uppercase tracking-wider font-semibold border-l border-slate-300 flex items-center gap-1 ${layout === "tree" ? "bg-[#0B1D3A] text-white" : "bg-white text-slate-600 hover:bg-slate-50"}`}
              title="Tampilan hierarki reports_to"
            ><GitBranch size={11} /> Hierarki</button>
          </div>
          <button type="button" data-testid="org-zoom-out" onClick={() => setZoom((z) => Math.max(0.4, +(z - 0.1).toFixed(2)))} className="w-7 h-7 border border-slate-300 hover:bg-slate-100 flex items-center justify-center text-slate-600" title="Perkecil"><ZoomOut size={13} /></button>
          <span className="text-[10px] tabular-nums text-slate-500 w-10 text-center">{Math.round(zoom * 100)}%</span>
          <button type="button" data-testid="org-zoom-in" onClick={() => setZoom((z) => Math.min(1.6, +(z + 0.1).toFixed(2)))} className="w-7 h-7 border border-slate-300 hover:bg-slate-100 flex items-center justify-center text-slate-600" title="Perbesar"><ZoomIn size={13} /></button>
          <button type="button" data-testid="org-zoom-reset" onClick={() => setZoom(1)} className="h-7 px-2 border border-slate-300 hover:bg-slate-100 flex items-center gap-1 text-[10px] uppercase tracking-wider text-slate-600" title="Reset zoom"><Maximize2 size={11} /> Reset</button>
        </div>
      </div>

      <div className="overflow-auto" style={{ maxHeight: "calc(100vh - 200px)" }}>
        <div
          className="relative"
          style={{ transform: `scale(${zoom})`, transformOrigin: "top left", padding: "16px", width: "max-content", minWidth: "100%" }}
        >
          {layout === "matrix" ? (
            <MatrixView
              columns={divisionColumns}
              bands={bands}
              q={q}
              canManage={canManage}
              canEditTiers={canEditTiers}
              onEditTier={openEdit}
              onSelect={onSelect}
              viewerId={viewerId}
              canSeeAllLevels={canSeeAllLevels}
            />
          ) : (
            <TreeView
              roots={roots}
              bands={bands}
              maxDepth={treeMaxDepth}
              q={q}
              canManage={canManage}
              canEditTiers={canEditTiers}
              onEditTier={openEdit}
              onSelect={onSelect}
              viewerId={viewerId}
              canSeeAllLevels={canSeeAllLevels}
            />
          )}
        </div>
      </div>

      {editing && (
        <TierEditor
          editing={editing}
          setEditing={setEditing}
          onSave={saveTier}
          onClose={close}
          maxDepth={maxDepth}
        />
      )}

      <style>{ORG_CHART_CSS}</style>
    </div>
  );
}

/* -------- MatrixView: single unified CSS grid with cross-division reports_to arrows -------- */
function MatrixView({ columns, bands, q, canManage, canEditTiers, onEditTier, onSelect, viewerId, canSeeAllLevels }) {
  const COL_W = 250;
  const ROW_MIN = 128;
  const cols = columns.length;
  const gridRef = useRef(null);
  const [arrows, setArrows] = useState([]);
  const [svgSize, setSvgSize] = useState({ w: 0, h: 0 });

  // Flat list of users + id→user lookup for reports_to resolution
  const allUsers = useMemo(() => columns.flatMap((c) => c.users), [columns]);
  const userMap = useMemo(() => {
    const m = {};
    allUsers.forEach((u) => { m[u.id] = u; });
    return m;
  }, [allUsers]);

  useLayoutEffect(() => {
    const measure = () => {
      const grid = gridRef.current;
      if (!grid) return;
      const gridRect = grid.getBoundingClientRect();
      const boxRects = {};
      grid.querySelectorAll("[data-user-id]").forEach((el) => {
        const r = el.getBoundingClientRect();
        boxRects[el.dataset.userId] = {
          cx: r.left + r.width / 2 - gridRect.left,
          top: r.top - gridRect.top,
          bottom: r.bottom - gridRect.top,
        };
      });
      // Only draw arrows that cross division columns (same-division are handled by the spine)
      const list = [];
      allUsers.forEach((u) => {
        if (!u.reports_to) return;
        const parent = userMap[u.reports_to];
        if (!parent) return;
        if (parent.division === u.division) return;
        const from = boxRects[parent.id];
        const to = boxRects[u.id];
        if (!from || !to) return;
        list.push({
          id: `${parent.id}-${u.id}`,
          x1: from.cx, y1: from.bottom,
          x2: to.cx, y2: to.top,
          parentName: parent.name,
          parentDivision: parent.division,
          childName: u.name,
          childDivision: u.division,
        });
      });
      setArrows(list);
      setSvgSize({ w: grid.scrollWidth, h: grid.scrollHeight });
    };
    measure();
    const ro = new ResizeObserver(measure);
    if (gridRef.current) ro.observe(gridRef.current);
    return () => ro.disconnect();
  }, [columns, allUsers, userMap]);

  if (cols === 0) return <div className="text-xs italic text-slate-400 py-8 text-center">Belum ada personil untuk ditampilkan.</div>;

  const depthToRow = (d) => Math.min(4, Math.max(2, d + 2));
  const hasAtLevel = (col, lv) => col.users.some((u) => u.level === lv);

  return (
    <div
      ref={gridRef}
      className="matrix-grid relative"
      data-testid="matrix-view"
      style={{
        display: "grid",
        gridTemplateColumns: `84px repeat(${cols}, ${COL_W}px)`,
        gridTemplateRows: `auto repeat(3, minmax(${ROW_MIN}px, max-content))`,
        gap: 0,
      }}
    >
      {/* Corner (empty above tier column, below toolbar) */}
      <div style={{ gridColumn: 1, gridRow: 1, borderBottom: "1px solid #cbd5e1", borderRight: "1px solid #cbd5e1" }} />

      {/* Division header row */}
      {columns.map((c, ci) => (
        <div
          key={c.name}
          style={{ gridColumn: ci + 2, gridRow: 1 }}
          className="text-center px-2 py-3 border-b border-slate-300 border-l border-slate-200 bg-white"
          data-testid={`matrix-col-header-${c.name}`}
        >
          <span className={`inline-block px-3 py-1 text-[11px] font-bold uppercase tracking-wider ${DIVISION_COLOR[c.name] || "bg-slate-600 text-white"}`}>
            {c.name}
          </span>
          <div className="text-[9px] text-slate-500 mt-1 tabular-nums">{c.users.length} personil</div>
        </div>
      ))}

      {/* Tier row backgrounds spanning across all division columns (behind content) */}
      {bands.map((b) => (
        <div
          key={`bg-${b.id}`}
          style={{
            gridColumn: `2 / ${cols + 2}`,
            gridRow: `${depthToRow(b.startD)} / ${depthToRow(b.endD) + 1}`,
            background: b.bg_color,
            zIndex: 0,
          }}
        />
      ))}

      {/* Tier labels in left column */}
      {bands.map((b) => (
        <div
          key={`label-${b.id}`}
          style={{
            gridColumn: 1,
            gridRow: `${depthToRow(b.startD)} / ${depthToRow(b.endD) + 1}`,
            background: b.bg_color,
            zIndex: 1,
          }}
          className="flex items-center justify-center border-r border-slate-300 border-b border-slate-200 relative group"
          data-testid={"org-band-" + b.id}
        >
          <div style={{ writingMode: "vertical-rl", transform: "rotate(180deg)" }} className="text-[11px] font-bold uppercase tracking-wider text-slate-700 px-1">
            {b.label}
          </div>
          {canEditTiers && (
            <div className="absolute top-1 right-1 opacity-0 group-hover:opacity-100 transition-opacity">
              <button
                type="button"
                data-testid={"btn-edit-tier-" + b.id}
                onClick={() => onEditTier(b)}
                className="w-5 h-5 bg-white border border-slate-300 hover:border-[#FF5A1F] flex items-center justify-center"
                title="Edit tier"
              ><Pencil size={10} className="text-slate-600" /></button>
            </div>
          )}
        </div>
      ))}

      {/* Column spines: one continuous vertical line per division */}
      {columns.map((c, ci) => {
        const levels = [1, 2, 3].filter((lv) => hasAtLevel(c, lv));
        if (levels.length < 2) return null;
        const startLv = levels[0];
        const endLv = levels[levels.length - 1];
        return (
          <div
            key={`spine-${c.name}`}
            aria-hidden="true"
            style={{
              gridColumn: ci + 2,
              gridRow: `${startLv + 1} / ${endLv + 2}`,
              justifySelf: "center",
              alignSelf: "stretch",
              width: 0,
              borderLeft: "1.5px solid #94a3b8",
              zIndex: 0,
              pointerEvents: "none",
            }}
          />
        );
      })}

      {/* Content cells */}
      {columns.map((c, ci) =>
        [1, 2, 3].map((lv) => {
          const cellUsers = c.users.filter((u) => u.level === lv);
          return (
            <div
              key={`${c.name}-${lv}`}
              data-testid={`matrix-cell-${c.name}-lv${lv}`}
              style={{ gridColumn: ci + 2, gridRow: lv + 1, zIndex: 1 }}
              className="matrix-cell relative flex flex-col items-center justify-start gap-3 px-2 py-3 border-l border-slate-200 border-b border-slate-100"
            >
              {cellUsers.map((u) => (
                <MatrixBox key={u.id} node={u} q={q} canManage={canManage} onSelect={onSelect} showLevel={canSeeAllLevels || u.id === viewerId} />
              ))}
            </div>
          );
        })
      )}

      {/* Cross-division reports_to arrows (SVG overlay) */}
      {svgSize.w > 0 && arrows.length > 0 && (
        <svg
          aria-hidden="true"
          className="pointer-events-none absolute top-0 left-0"
          width={svgSize.w}
          height={svgSize.h}
          style={{ zIndex: 3, overflow: "visible", pointerEvents: "none" }}
          data-testid="cross-division-arrows"
        >
          <defs>
            <marker id="arrow-head" viewBox="0 0 10 10" refX="8" refY="5" markerWidth="6" markerHeight="6" orient="auto-start-reverse">
              <path d="M 0 0 L 10 5 L 0 10 z" fill="#0B1D3A" opacity="0.7" />
            </marker>
          </defs>
          {arrows.map((a) => {
            // Cubic bezier: fromBottom → downward control → upward control → toTop
            const dy = Math.max(24, (a.y2 - a.y1) * 0.4);
            const path = `M ${a.x1} ${a.y1} C ${a.x1} ${a.y1 + dy}, ${a.x2} ${a.y2 - dy}, ${a.x2} ${a.y2 - 3}`;
            return (
              <g key={a.id} className="cross-arrow-group">
                <path
                  d={path}
                  stroke="#0B1D3A"
                  strokeWidth="1.5"
                  strokeOpacity="0.6"
                  fill="none"
                  strokeDasharray="5,3"
                  markerEnd="url(#arrow-head)"
                  className="cross-arrow-path"
                >
                  <title>{`${a.parentName} (${a.parentDivision}) → ${a.childName} (${a.childDivision})`}</title>
                </path>
                {/* Wide invisible hit-target for better hover tooltip on thin dashed line */}
                <path
                  d={path}
                  stroke="transparent"
                  strokeWidth="14"
                  fill="none"
                  style={{ pointerEvents: "stroke" }}
                >
                  <title>{`${a.parentName} (${a.parentDivision}) → ${a.childName} (${a.childDivision})`}</title>
                </path>
              </g>
            );
          })}
        </svg>
      )}
    </div>
  );
}

/* -------- TreeView: measures actual box Y positions with useLayoutEffect
   so tier bands ALWAYS line up perfectly with the tree rows (regardless of
   box height variance from wrapping metadata). -------- */
function TreeView({ roots, bands, maxDepth, q, canManage, canEditTiers, onEditTier, onSelect, viewerId, canSeeAllLevels }) {
  const TOP_PAD = 24;
  const BOTTOM_PAD = 20;
  const scrollRef = useRef(null);
  const treeAreaRef = useRef(null);
  const [rowRects, setRowRects] = useState([]); // [{depth, top, bottom}]
  const [chartHeight, setChartHeight] = useState(400);

  useLayoutEffect(() => {
    const measure = () => {
      const area = treeAreaRef.current;
      if (!area) return;
      const boxes = area.querySelectorAll("[data-depth]");
      if (boxes.length === 0) { setRowRects([]); return; }
      const areaRect = area.getBoundingClientRect();
      const perDepth = new Map();
      boxes.forEach((el) => {
        const d = parseInt(el.dataset.depth, 10);
        const r = el.getBoundingClientRect();
        const top = r.top - areaRect.top;
        const bottom = r.bottom - areaRect.top;
        const cur = perDepth.get(d);
        if (!cur) perDepth.set(d, { top, bottom });
        else perDepth.set(d, { top: Math.min(cur.top, top), bottom: Math.max(cur.bottom, bottom) });
      });
      const rects = Array.from(perDepth.entries())
        .map(([depth, r]) => ({ depth, ...r }))
        .sort((a, b) => a.depth - b.depth);
      setRowRects(rects);
      setChartHeight(area.scrollHeight);
    };
    measure();
    // Re-measure on window resize
    const ro = new ResizeObserver(measure);
    if (treeAreaRef.current) ro.observe(treeAreaRef.current);
    return () => ro.disconnect();
  }, [roots, q]);

  // Compute band Y ranges from measured row positions.
  // Between adjacent measured rows, split the gap at midpoint so bands touch.
  const bandsSized = useMemo(() => {
    if (rowRects.length === 0) {
      // Fallback: uniform rows before measurement completes
      const ROW_H = 128;
      return bands.map((b) => {
        const startY = TOP_PAD + b.startD * ROW_H;
        const endY = TOP_PAD + (b.endD + 1) * ROW_H;
        return { ...b, startY, endY, height: Math.max(0, endY - startY) };
      }).filter((b) => b.height > 0);
    }
    // Midpoints between consecutive rows
    const midpoints = [];
    midpoints.push(0); // top of chart
    for (let i = 0; i < rowRects.length - 1; i++) {
      midpoints.push((rowRects[i].bottom + rowRects[i + 1].top) / 2);
    }
    midpoints.push(chartHeight); // bottom of chart

    // For each depth d present in rowRects, band boundary [midpoints[d_idx], midpoints[d_idx+1]]
    const depthToIdx = new Map();
    rowRects.forEach((r, i) => depthToIdx.set(r.depth, i));

    return bands.map((b) => {
      const startIdx = depthToIdx.get(b.startD);
      const endIdx = depthToIdx.get(b.endD);
      // If a band references a depth not present in the tree, skip it
      if (startIdx == null || endIdx == null) return null;
      const startY = midpoints[startIdx];
      const endY = midpoints[endIdx + 1];
      return { ...b, startY, endY, height: Math.max(0, endY - startY) };
    }).filter(Boolean);
  }, [bands, rowRects, chartHeight]);

  return (
    <div ref={scrollRef} className="flex items-stretch relative" style={{ minHeight: chartHeight + "px" }}>
      <aside className="sticky left-0 z-10 flex flex-col shrink-0 self-stretch" style={{ width: "84px" }}>
        {bandsSized.length === 0 && <div className="text-[10px] italic text-slate-400 p-2">Belum ada tier.</div>}
        {bandsSized.map((b) => (
          <div
            key={b.id}
            className="flex items-center justify-center border-r border-slate-200 relative group"
            style={{ height: b.height + "px", background: b.bg_color }}
            data-testid={"org-band-" + b.id}
          >
            <div style={{ writingMode: "vertical-rl", transform: "rotate(180deg)" }} className="text-[11px] font-bold uppercase tracking-wider text-slate-700 px-1">
              {b.label}
            </div>
            {canEditTiers && (
              <div className="absolute top-1 right-1 opacity-0 group-hover:opacity-100 transition-opacity">
                <button
                  type="button"
                  data-testid={"btn-edit-tier-" + b.id}
                  onClick={() => onEditTier(b)}
                  className="w-5 h-5 bg-white border border-slate-300 hover:border-[#FF5A1F] flex items-center justify-center"
                ><Pencil size={10} className="text-slate-600" /></button>
              </div>
            )}
          </div>
        ))}
      </aside>
      <div ref={treeAreaRef} className="relative flex-1 min-w-max" style={{ minHeight: chartHeight + "px" }}>
        {bandsSized.map((b) => (
          <div key={b.id} className="absolute left-0 right-0 pointer-events-none" style={{ top: b.startY + "px", height: b.height + "px", background: b.bg_color, zIndex: 0 }} />
        ))}
        <div className="relative" style={{ paddingTop: TOP_PAD + "px", paddingLeft: "32px", paddingRight: "32px", paddingBottom: BOTTOM_PAD + "px", zIndex: 1 }}>
          <div className="org-chart flex flex-wrap justify-center gap-8 items-start">
            {roots.map((r) => (
              <div key={r.id} className="org-tree">
                <ul>
                  {React.createElement(OrgChartNode, { node: r, q, canManage, onSelect, depth: 0, viewerId, canSeeAllLevels })}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

function TierEditor({ editing, setEditing, onSave, onClose, maxDepth }) {
  const swatches = ["#EEF2FF", "#DBEAFE", "#BFDBFE", "#93C5FD", "#FEF3C7", "#FED7AA", "#FBCFE8", "#D1FAE5"];
  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white border border-slate-300 w-full max-w-md" onClick={(e) => e.stopPropagation()} data-testid="tier-editor-dialog">
        <div className="px-4 py-3 border-b border-slate-200 flex items-center justify-between">
          <h3 className="font-display text-base text-[#0B1D3A]">{editing.mode === "create" ? "Tambah Tier" : "Edit Tier"}</h3>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-700"><X size={16} /></button>
        </div>
        <div className="p-4 space-y-3">
          <div>
            <label className="text-[10px] uppercase tracking-wider text-slate-600 font-semibold">Label</label>
            <input
              data-testid="tier-input-label"
              type="text"
              value={editing.label}
              onChange={(e) => setEditing({ ...editing, label: e.target.value })}
              placeholder="Contoh: C-Suite, Manager / Head"
              className="w-full mt-1 h-9 px-2 border border-slate-300 text-sm"
              autoFocus
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-[10px] uppercase tracking-wider text-slate-600 font-semibold">Depth Start</label>
              <input
                data-testid="tier-input-start"
                type="number"
                min="0"
                max="20"
                value={editing.depth_start}
                onChange={(e) => setEditing({ ...editing, depth_start: parseInt(e.target.value, 10) || 0 })}
                className="w-full mt-1 h-9 px-2 border border-slate-300 text-sm tabular-nums"
              />
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-wider text-slate-600 font-semibold">Depth End</label>
              <input
                data-testid="tier-input-end"
                type="number"
                min="0"
                max="99"
                value={editing.depth_end}
                onChange={(e) => setEditing({ ...editing, depth_end: parseInt(e.target.value, 10) || 0 })}
                className="w-full mt-1 h-9 px-2 border border-slate-300 text-sm tabular-nums"
              />
            </div>
          </div>
          <div className="text-[10px] text-slate-500">Kedalaman tree saat ini: 0…{maxDepth}. Depth 0 = pucuk. Range depth_start–depth_end menentukan tinggi band ini.</div>
          <div>
            <label className="text-[10px] uppercase tracking-wider text-slate-600 font-semibold">Warna Background</label>
            <div className="flex items-center gap-2 mt-1">
              <input
                data-testid="tier-input-color"
                type="text"
                value={editing.bg_color}
                onChange={(e) => setEditing({ ...editing, bg_color: e.target.value })}
                className="w-24 h-9 px-2 border border-slate-300 text-xs font-mono"
              />
              <div className="flex gap-1 flex-wrap">
                {swatches.map((c) => (
                  <button
                    key={c}
                    type="button"
                    onClick={() => setEditing({ ...editing, bg_color: c })}
                    style={{ background: c }}
                    className={"w-6 h-6 border " + (editing.bg_color === c ? "border-[#0B1D3A] border-2" : "border-slate-300")}
                  />
                ))}
              </div>
            </div>
          </div>
          <div className="p-3 border" style={{ background: editing.bg_color }}>
            <div className="text-[11px] font-bold uppercase tracking-wider text-slate-700 text-center">{editing.label || "(Preview)"}</div>
          </div>
        </div>
        <div className="px-4 py-3 border-t border-slate-200 flex items-center justify-end gap-2">
          <button onClick={onClose} className="h-8 px-3 border border-slate-300 text-xs uppercase tracking-wider hover:bg-slate-50">Batal</button>
          <button data-testid="tier-save-btn" onClick={onSave} className="h-8 px-3 bg-[#FF5A1F] hover:bg-[#e04a13] text-white text-xs uppercase tracking-wider flex items-center gap-1"><Check size={12} /> Simpan</button>
        </div>
      </div>
    </div>
  );
}

function countNodes(roots) {
  let n = 0;
  const walk = (arr) => arr.forEach((x) => { n += 1; if (x.children && x.children.length) walk(x.children); });
  walk(roots);
  return n;
}

function MatrixBox({ node, q, canManage, onSelect, showLevel = true }) {
  const matches = !q || (node.name || "").toLowerCase().includes(q) || (node.role_title || "").toLowerCase().includes(q) || (node.division || "").toLowerCase().includes(q);
  const roleTitle = node.position || node.role_title || (node.level === 1 ? "Director" : node.level === 2 ? "Manager" : "Staff");
  const subLabel = node.division === "Project" && node.sub_division ? node.sub_division : "";
  return (
    <button
      type="button"
      disabled={!canManage}
      onClick={() => canManage && onSelect(node.id)}
      data-testid={"matrix-box-" + node.id}
      data-user-id={node.id}
      className={"org-box " + (matches ? "" : "opacity-40 grayscale ") + (canManage ? "cursor-pointer" : "cursor-default")}
    >
      <div className="org-box-role">{roleTitle}</div>
      <div className="org-box-sep" />
      <div className="org-box-name" title={node.name}>{node.name}</div>
      <div className="org-box-meta">
        <span className={"org-box-badge " + (DIVISION_COLOR[node.division] || "bg-slate-600 text-white")}>{node.division}</span>
        {showLevel && <span className="org-box-badge bg-white/20 text-white">Lv{node.level}</span>}
        {subLabel ? <span className="org-box-sub" title={subLabel}>· {subLabel}</span> : null}
      </div>
    </button>
  );
}

const ORG_CHART_CSS = `
.org-tree { display: inline-block; }
.org-tree > ul { padding: 0; margin: 0; display: flex; justify-content: center; list-style: none; }
.org-tree ul ul { padding-top: 32px; position: relative; display: flex; justify-content: center; list-style: none; margin: 0; }
.org-tree li { list-style: none; text-align: center; position: relative; padding: 32px 8px 0; display: flex; flex-direction: column; align-items: center; }
.org-tree > ul > li { padding-top: 0; }

.org-tree ul ul > li::before,
.org-tree ul ul > li::after {
  content: ''; position: absolute; top: 0; right: 50%;
  border-top: 1.5px solid #1e3a8a; width: 50%; height: 32px;
}
.org-tree ul ul > li::after {
  left: 50%; right: auto;
  border-left: 1.5px solid #1e3a8a;
  border-top: 1.5px solid #1e3a8a;
}
.org-tree ul ul > li:only-child::before {
  content: '';
  position: absolute;
  top: 0;
  right: auto;
  left: 50%;
  border: 0;
  border-left: 1.5px solid #1e3a8a;
  width: 0;
  height: 32px;
  border-radius: 0;
}
.org-tree ul ul > li:only-child::after { display: none; }
.org-tree ul ul > li:only-child { padding-top: 32px; }
.org-tree ul ul > li:first-child::before { border: 0 none; }
.org-tree ul ul > li:last-child::after { border: 0 none; }
.org-tree ul ul > li:first-child::after { border-radius: 2px 0 0 0; }
.org-tree ul ul > li:last-child::before {
  border-right: 1.5px solid #1e3a8a;
  border-radius: 0 2px 0 0;
}

.org-tree li > ul::before {
  content: ''; position: absolute; top: 0; left: 50%;
  border-left: 1.5px solid #1e3a8a; width: 0; height: 32px;
}

/* Matrix column spine is rendered as a real grid item (see MatrixView).
   Boxes (z-index 2) overlap the spine (z-index 0), so the line is visible
   only in padding + gap areas between boxes — a continuous chain effect. */

/* Box styling — shared between tree and matrix layouts */
.org-box {
  display: flex; flex-direction: column; align-items: stretch;
  width: 220px;
  padding: 10px 12px;
  background: #2f3fa5;
  border: 1px solid #1e2a78;
  color: #fff;
  text-align: center;
  transition: transform 0.15s ease, box-shadow 0.15s ease, background 0.15s ease;
  position: relative; z-index: 2;
  box-shadow: 0 2px 6px rgba(30, 42, 120, 0.25);
}
.org-box:not(:disabled):hover {
  background: #3949bc;
  transform: translateY(-2px);
  box-shadow: 0 6px 18px rgba(30, 42, 120, 0.4);
}
.org-box-role { font-size: 12px; font-weight: 700; color: #fff; line-height: 1.2; padding: 2px 0; }
.org-box-sep { height: 1px; background: rgba(255,255,255,0.4); margin: 6px 0; }
.org-box-name {
  font-size: 11px; font-weight: 500; color: #E0E7FF; line-height: 1.25; padding: 2px 0;
  overflow: hidden; text-overflow: ellipsis; white-space: nowrap;
}
.org-box-meta { display: flex; align-items: center; justify-content: center; gap: 4px; margin-top: 6px; flex-wrap: wrap; }
.org-box-badge { padding: 1px 4px; font-size: 8px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.05em; }
.org-box-sub { font-size: 8px; color: #E0E7FF; }
.org-box-count { font-size: 8px; color: rgba(255,255,255,0.7); margin-left: auto; font-family: tabular-nums; }
.org-box:disabled { opacity: 1; }
`;
